import React, { useState } from "react";
import { Helmet } from "react-helmet";
import styles from "./ContactUsPage2.module.scss";
import { backgroundImg } from "src/assets/assetsExport";
import { NavLink } from "react-router-dom";
import { submitContact } from "../../api/contact";
import GoogleReviewsEmbed from "../../components/GoogleReviews/GoogleReviewsEmbed";

interface ContactUsPage2Props {}

export default function ContactUsPage2(
  props: ContactUsPage2Props
): JSX.Element {
  const [formMode, setFormMode] = useState<"general" | "valuation">("general");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const easeTech =
    "transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSubmitting(true);

    try {
      const subjectPrefix =
        formMode === "general" ? "General Enquiry" : "Book Valuation";
      const fullSubject = subject.trim()
        ? `${subjectPrefix}: ${subject.trim()}`
        : subjectPrefix;

      await submitContact({
        name: name.trim(),
        email: email.trim(),
        subject: fullSubject,
        message: message.trim(),
        sourcePage: "/contact-us",
      });

      setSuccess(true);
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to send your message"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Letting Engine</title>
        <meta
          name="description"
          content="Get in touch with Letting Engine. Whether you need a valuation, tenant support, or careers information, our Mayfair team is here to help."
        />
        <meta
          name="keywords"
          content="residential property, real estate, london, contact us, careers, valuation, support"
        />
      </Helmet>

      {/* HERO */}
      <section className="min-h-[90vh] pt-40 pb-20 w-full relative flex items-center justify-center overflow-hidden text-center">
        <img
          src={backgroundImg.meeting1}
          alt="meeting"
          className="absolute inset-0 w-full h-full object-cover opacity-20 filter grayscale contrast-125 pointer-events-none z-0"
        />
        <div className="relative z-10 max-w-4xl px-6">
          <h1 className="font-teko leading-tight mb-6 tracking-[-2px] text-6xl">
            <span className="stroke-1 text-text-slate">Contact us</span>
          </h1>
          <p className="font-manrope text-base leading-relaxed text-text-slate max-w-2xl mx-auto">
            Ready to let your luxury property or find your dream rental in
            London? Get in touch with our team today to discuss your letting
            needs or schedule a property viewing.
          </p>
          <div className="flex justify-center flex-col sm:flex-row gap-8 sm:gap-16 border-t border-t-solid border-t-[rgba(0,0,0,0.1)] pt-8 mt-10">
            <div className="text-center">
              <h3 className="font-teko text-5xl text-brand-yellow">Email Us</h3>
              <p className="font-manrope text-xs uppercase tracking-wider">
                contact@lettingengine.com
              </p>
            </div>
            <div className="text-center">
              <h3 className="font-teko text-5xl text-brand-yellow">Call</h3>
              <p className="font-manrope text-xs uppercase tracking-wider">
                +44 (0)20 3907 4025
              </p>
            </div>
            <div className="text-center">
              <h3 className="font-teko text-5xl text-brand-yellow">Visit Us</h3>
              <p className="font-manrope text-xs uppercase tracking-wider">
                16 Berkeley Street, Mayfair W1J 8DZ
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 1. CONTACT & VALUATION */}
      <section className="py-32 relative">
        <div className="max-w-[1440px] mx-auto px-[5%] grid grid-cols-1 md:grid-cols-[1fr_1.2fr] gap-40 md:gap-12 lg:gap-24 items-start ">
          {/* LEFT: INFO */}
          <div className="flex flex-col gap-12 order-2 md:order-1">
            <div className="relative pl-8 border-l-2 border-l-solid border-l-brand-yellow">
              <span className="font-manrope text-xs uppercase tracking-widest text-text-slate block mb-2">
                Visit us
              </span>
              <h2 className="font-teko text-5xl text-text-obsidian">
                Our office
              </h2>
            </div>
            <div className="bg-bg-white p-10 border border-solid border-[rgba(0,0,0,0.05)] relative transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_90%,95%_100%,0_100%)] hover:translate-y-[-5px] hover:shadow-soft hover:border-brand-yellow">
              <h3 className="font-teko text-2xl mb-2">London Office</h3>
              <p className="font-manrope text-lg text-text-slate leading-relaxed">
                16 Berkeley Street
                <br />
                Mayfair, London
                <br />
                W1J 8DZ
                <br />
                UK
              </p>
            </div>
            <div className="bg-bg-white p-10 border border-solid border-[rgba(0,0,0,0.05)] relative transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_90%,95%_100%,0_100%)] hover:translate-y-[-5px] hover:shadow-soft hover:border-brand-yellow">
              <h3 className="font-teko text-2xl mb-2">Direct Line</h3>
              <p className="font-manrope text-lg text-text-slate leading-relaxed">
                +44 (0)20 3907 4025
                <br />
                Mon - Fri: 08:00 - 20:00
                <br />
                Sat: 10:00 - 16:00
              </p>
            </div>
            <div className="bg-bg-white p-10 border border-solid border-[rgba(0,0,0,0.05)] relative transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_90%,95%_100%,0_100%)] hover:translate-y-[-5px] hover:shadow-soft hover:border-brand-yellow">
              <h3 className="font-teko text-2xl mb-2">Email</h3>
              <div className="font-manrope text-base text-text-obsidian mb-4 flex flex-col gap-1">
                <div>
                  <span className="font-semibold">Email:</span>{" "}
                  <a
                    href="mailto:contact@lettingengine.com"
                    className="text-brand-yellow hover:underline"
                  >
                    contact@lettingengine.com
                  </a>
                </div>
                <div>
                  <span className="font-semibold">Feedback:</span>{" "}
                  <a
                    href="mailto:feedback@lettingengine.com"
                    className="text-brand-yellow hover:underline"
                  >
                    feedback@lettingengine.com
                  </a>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-12 pt-8">
              {/* Protocol 01 */}
              <div className="pl-16 border-l border-black/10 relative">
                <span className="font-['Teko'] text-5xl text-black/5 absolute left-2 top-[-1.5rem] leading-none select-none">
                  01
                </span>
                <h3 className="font-['Teko'] text-3xl uppercase mb-3 text-[#111]">
                  General enquiries
                </h3>
                <p className="text-[#555] leading-relaxed">
                  For information about our services or how we work,
                  please email us at{" "}
                  <a
                    href="mailto:contact@lettingengine.com"
                    className="font-bold text-[#111] border-b border-[#FFC400] hover:bg-[#FFC400] transition-colors"
                  >
                    contact@lettingengine.com
                  </a>
                  . Our knowledgeable team is ready to assist you.
                </p>
              </div>

              {/* Protocol 02 */}
              <div className="pl-16 border-l border-black/10 relative">
                <span className="font-['Teko'] text-5xl text-black/5 absolute left-2 top-[-1.5rem] leading-none select-none">
                  02
                </span>
                <h3 className="font-['Teko'] text-3xl uppercase mb-3 text-[#111]">
                  Property enquiries
                </h3>
                <p className="text-[#555] leading-relaxed">
                  If you have questions about a particular property,
                  please contact us through the{" "}
                  <span className="font-semibold text-[#111]">
                    property listing page
                  </span>
                  .
                </p>
              </div>

              {/* Protocol 03: Careers Special Card */}
              <div
                className={`group bg-[#111] text-white p-10 hover:-translate-y-2 shadow-2xl ${easeTech}`}
              >
                <div className="">
                  <span className="block text-xs text-[#FFC400] uppercase tracking-[0.2em] mb-4 font-bold">
                    03
                  </span>
                  <h3 className="font-['Teko'] text-4xl uppercase mb-4 text-white group-hover:text-[#FFC400] transition-colors">
                    Careers
                  </h3>
                  <p className="text-gray-400 mb-8 text-lg">
                    Interested in joining our team? We are always looking for
                    driven people who care about exceptional service.
                  </p>

                  <NavLink
                    to="/careers"
                    className={`inline-block bg-white text-[#111] px-8 py-3 font-['Teko'] text-2xl uppercase tracking-wide hover:bg-[#FFC400] hover:text-black ${easeTech} [clip-path:polygon(0_0,100%_0,100%_80%,92%_100%,0_100%)]`}
                  >
                    View Openings
                  </NavLink>
                </div>
              </div>

              {/* Protocol 04 */}
              <div className="pl-16 border-l border-black/10 relative">
                <span className="font-['Teko'] text-5xl text-black/5 absolute left-2 top-[-1.5rem] leading-none select-none">
                  04
                </span>
                <h3 className="font-['Teko'] text-3xl uppercase mb-3 text-[#111]">
                  Feedback
                </h3>
                <p className="text-[#555] leading-relaxed">
                  We value your input to improve our service. If you have
                  suggestions, please direct them to{" "}
                  <a
                    href="mailto:feedback@lettingengine.com"
                    className="font-bold text-[#111] border-b border-[#FFC400] hover:bg-[#FFC400] transition-colors"
                  >
                    feedback@lettingengine.com
                  </a>
                  .
                </p>
              </div>
            </div>
          </div>
          {/* RIGHT: DUAL FORM */}
          <div className="bg-bg-white p-16 border border-solid border-[rgba(0,0,0,0.05)] shadow-[0_30px_60px_rgba(0,0,0,0.05)] clip-path-[polygon(5%_0,100%_0,100%_95%,95%_100%,0_100%,0_5%)] md:p-8 md:sticky top-28 self-start order-1 md:order-2">
            {/* Toggle */}
            <div className="flex bg-bg-porcelain border border-solid border-[#ddd] p-1.5 mb-12 transform skew-x-[var(--skew)] w-fit">
              <button
                className={`py-3 px-10 font-teko text-xl uppercase cursor-pointer transition-all duration-300 relative overflow-hidden bg-transparent z-[1] ${
                  formMode === "general"
                    ? "text-brand-yellow"
                    : "text-text-slate"
                }`}
                onClick={() => setFormMode("general")}
              >
                {formMode === "general" && (
                  <span className="absolute inset-0 bg-text-obsidian z-0 skew-x-[10deg] pointer-events-none"></span>
                )}
                <span className="relative z-10">General Enquiry</span>
              </button>
              <button
                className={`py-3 px-10 font-teko text-xl uppercase cursor-pointer transition-all duration-300 relative overflow-hidden bg-transparent z-[1] ${
                  formMode === "valuation"
                    ? "text-brand-yellow"
                    : "text-text-slate"
                }`}
                onClick={() => setFormMode("valuation")}
              >
                {formMode === "valuation" && (
                  <span className="absolute inset-0 bg-text-obsidian z-0 skew-x-[10deg] pointer-events-none"></span>
                )}
                <span className="relative z-10">Book Valuation</span>
              </button>
            </div>
            {/* Form */}
            <form className="grid gap-6" onSubmit={handleSubmit}>
              {success && (
                <p className="font-manrope text-sm text-green-700 bg-green-50 p-4 border border-green-200">
                  Thank you — your message has been sent. We will be in touch
                  shortly.
                </p>
              )}
              {error && (
                <p className="font-manrope text-sm text-red-700 bg-red-50 p-4 border border-red-200">
                  {error}
                </p>
              )}
              <div className={styles.inputGroup}>
                <label htmlFor="fullName" className={styles.formLabel}>
                  Name
                </label>
                <input
                  type="text"
                  className={styles.formInput}
                  placeholder="Full Name"
                  id="fullName"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={submitting}
                />
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="emailAddress" className={styles.formLabel}>
                  Email
                </label>
                <input
                  type="email"
                  className={styles.formInput}
                  placeholder="Email Address"
                  id="emailAddress"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={submitting}
                />
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor="subjectAddress" className={styles.formLabel}>
                  {formMode === "general" ? "Subject" : "Property address"}
                </label>
                <input
                  type="text"
                  className={styles.formInput}
                  placeholder={
                    formMode === "general"
                      ? "Enter subject..."
                      : "Enter full property address..."
                  }
                  id="subjectAddress"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  disabled={submitting}
                />
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="message" className={styles.formLabel}>
                  Message
                </label>
                <textarea
                  className={styles.formTextarea}
                  rows={4}
                  placeholder="Your message..."
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  disabled={submitting}
                ></textarea>
              </div>
              <button
                type="submit"
                className={styles.submitButton}
                disabled={submitting}
              >
                {submitting ? "Sending…" : "Send enquiry"}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* GET IN TOUCH */}
      <GoogleReviewsEmbed variant="dark" />
    </>
  );
}
